/**
 * OverdueVaccineLogsPage - Full page view for overdue vaccine notification logs
 * Table format matching the Audit Log design with stats cards and filters
 * RESTful: uses URL params for filters and pagination
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Clock, 
  AlertCircle, 
  Send, 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  RefreshCw,
  Filter,
  X,
  FileText,
  Search,
  SkipForward,
  ChevronDown
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';

interface LogEntry {
  id: string;
  event_type: string;
  message: string;
  details: Record<string, any> | null;
  error_message: string | null;
  created_at: string;
}

interface StatsResponse {
  total: number;
  timer_fired?: number;
  vaccines_found?: number;
  pets_found?: number;
  notification_scheduled?: number;
  notification_sent?: number;
  notification_skipped?: number;
  error?: number;
}

interface LogsResponse {
  logs: LogEntry[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
  stats: StatsResponse;
}

const overdueEventTypeConfig: Record<string, { icon: React.ReactNode; color: string; bgColor: string; label: string; value: string }> = {
  timer_fired: {
    icon: <Clock className="w-4 h-4" />,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    label: 'Daily Check',
    value: 'timer_fired'
  },
  vaccines_found: {
    icon: <Calendar className="w-4 h-4" />,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    label: 'Vaccine Search',
    value: 'vaccines_found'
  },
  notification_scheduled: {
    icon: <Clock className="w-4 h-4" />,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    label: 'Scheduled',
    value: 'notification_scheduled'
  },
  notification_sent: {
    icon: <Send className="w-4 h-4" />,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    label: 'Sent',
    value: 'notification_sent'
  },
  notification_skipped: {
    icon: <SkipForward className="w-4 h-4" />,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    label: 'Skipped',
    value: 'notification_skipped'
  },
  error: {
    icon: <AlertCircle className="w-4 h-4" />,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    label: 'Error',
    value: 'error'
  }
};

const appointmentEventTypeConfig: Record<string, { icon: React.ReactNode; color: string; bgColor: string; label: string; value: string }> = {
  notification_sent: {
    icon: <Send className="w-4 h-4" />,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    label: 'Sent',
    value: 'notification_sent'
  },
  notification_skipped: {
    icon: <SkipForward className="w-4 h-4" />,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    label: 'Skipped',
    value: 'notification_skipped'
  },
  error: {
    icon: <AlertCircle className="w-4 h-4" />,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    label: 'Error',
    value: 'error'
  }
};

const needsAppointmentEventTypeConfig: Record<string, { icon: React.ReactNode; color: string; bgColor: string; label: string; value: string }> = {
  timer_fired: {
    icon: <Clock className="w-4 h-4" />,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    label: 'Daily Check',
    value: 'timer_fired'
  },
  pets_found: {
    icon: <Calendar className="w-4 h-4" />,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    label: 'Pet Search',
    value: 'pets_found'
  },
  notification_scheduled: {
    icon: <Clock className="w-4 h-4" />,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    label: 'Scheduled',
    value: 'notification_scheduled'
  },
  notification_sent: {
    icon: <Send className="w-4 h-4" />,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    label: 'Sent',
    value: 'notification_sent'
  },
  notification_skipped: {
    icon: <SkipForward className="w-4 h-4" />,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    label: 'Skipped',
    value: 'notification_skipped'
  },
  error: {
    icon: <AlertCircle className="w-4 h-4" />,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    label: 'Error',
    value: 'error'
  }
};


const overdueEventTypeOptions = [
  { value: '', label: 'All Events' },
  { value: 'timer_fired', label: 'Daily Check' },
  { value: 'vaccines_found', label: 'Vaccine Search' },
  { value: 'notification_scheduled', label: 'Scheduled' },
  { value: 'notification_sent', label: 'Sent' },
  { value: 'notification_skipped', label: 'Skipped' },
  { value: 'error', label: 'Error' }
];

const appointmentEventTypeOptions = [
  { value: '', label: 'All Events' },
  { value: 'notification_sent', label: 'Sent' },
  { value: 'notification_skipped', label: 'Skipped' },
  { value: 'error', label: 'Error' }
];

const needsAppointmentEventTypeOptions = [
  { value: '', label: 'All Events' },
  { value: 'timer_fired', label: 'Daily Check' },
  { value: 'pets_found', label: 'Pet Search' },
  { value: 'notification_scheduled', label: 'Scheduled' },
  { value: 'notification_sent', label: 'Sent' },
  { value: 'notification_skipped', label: 'Skipped' },
  { value: 'error', label: 'Error' }
];


const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const OverdueVaccineLogsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [logTypeDropdownOpen, setLogTypeDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const logTypeDropdownRef = useRef<HTMLDivElement>(null);
  
  const limit = 25;
  
  // Get values from URL params (RESTful)
  const page = parseInt(searchParams.get('page') || '1', 10);
  const eventTypeFilter = searchParams.get('event_type') || '';
  const searchQuery = searchParams.get('search') || '';
  const logType = searchParams.get('log_type') || 'all';

  // Combined event type options for 'all' view
  const allEventTypeOptions = [
    { value: '', label: 'All Events' },
    { value: 'timer_fired', label: 'Daily Check' },
    { value: 'vaccines_found', label: 'Vaccine Search' },
    { value: 'pets_found', label: 'Pet Search' },
    { value: 'notification_scheduled', label: 'Scheduled' },
    { value: 'notification_sent', label: 'Sent' },
    { value: 'notification_skipped', label: 'Skipped' },
    { value: 'error', label: 'Error' }
  ];

  const eventTypeOptions = logType === 'all'
    ? allEventTypeOptions
    : logType === 'overdue_vaccines'
      ? overdueEventTypeOptions
      : logType === 'needs_appointment'
        ? needsAppointmentEventTypeOptions
        : appointmentEventTypeOptions;
  
  // Combined config merges both
  const allEventTypeConfig = { ...overdueEventTypeConfig, ...needsAppointmentEventTypeConfig };
  
  const eventTypeConfig = logType === 'all'
    ? allEventTypeConfig
    : logType === 'overdue_vaccines'
      ? overdueEventTypeConfig
      : logType === 'needs_appointment'
        ? needsAppointmentEventTypeConfig
        : appointmentEventTypeConfig;
  
  // Helper to update URL params
  const updateParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams, { replace: true });
  };
  
  // Setters that update URL params
  const setPage = (p: number) => updateParams({ page: p === 1 ? null : p.toString() });
  const setEventTypeFilter = (v: string) => updateParams({ event_type: v || null, page: null });
  const setLogType = (v: string) => updateParams({ log_type: v || null, event_type: null, page: null });
  const setSearchQuery = (v: string) => updateParams({ search: v || null, page: null });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setDropdownOpen(false);
        }
        if (logTypeDropdownRef.current && !logTypeDropdownRef.current.contains(event.target as Node)) {
          setLogTypeDropdownOpen(false);
        }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // For 'all' view, fetch from both endpoints and merge
      if (logType === 'all') {
        const params1 = new URLSearchParams({ page: '1', limit: '100' });
        const params2 = new URLSearchParams({ page: '1', limit: '100' });
        if (eventTypeFilter) {
          params1.append('event_type', eventTypeFilter);
          params2.append('event_type', eventTypeFilter);
        }
        if (debouncedSearch) {
          params1.append('search', debouncedSearch);
          params2.append('search', debouncedSearch);
        }
        
        const [vaxRes, noVisitRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/v1/overdue-vaccine-triggers/logs?${params1}`, { headers }),
          fetch(`${API_ENDPOINTS.NO_VISIT_REMINDER_LOGS.LIST}?${params2}`, { headers })
        ]);
        
        if (!vaxRes.ok || !noVisitRes.ok) {
          throw new Error('Failed to fetch logs');
        }
        
        const [vaxData, noVisitData]: [LogsResponse, LogsResponse] = await Promise.all([
          vaxRes.json(),
          noVisitRes.json()
        ]);
        
        // Tag logs with source for display
        const taggedVaxLogs = vaxData.logs.map(log => ({ ...log, _source: 'vaccine' as const }));
        const taggedNoVisitLogs = noVisitData.logs.map(log => ({ ...log, _source: 'appt_due' as const }));
        
        // Merge and sort by date descending
        const allLogs = [...taggedVaxLogs, ...taggedNoVisitLogs]
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        
        // Paginate client-side
        const startIdx = (page - 1) * limit;
        const paginatedLogs = allLogs.slice(startIdx, startIdx + limit);
        const combinedTotal = allLogs.length;
        
        // Combine stats
        const combinedStats: StatsResponse = {
          total: (vaxData.stats.total || 0) + (noVisitData.stats.total || 0),
          timer_fired: (vaxData.stats.timer_fired || 0) + (noVisitData.stats.timer_fired || 0),
          vaccines_found: vaxData.stats.vaccines_found || 0,
          pets_found: noVisitData.stats.pets_found || 0,
          notification_scheduled: (vaxData.stats.notification_scheduled || 0) + (noVisitData.stats.notification_scheduled || 0),
          notification_sent: (vaxData.stats.notification_sent || 0) + (noVisitData.stats.notification_sent || 0),
          notification_skipped: (vaxData.stats.notification_skipped || 0) + (noVisitData.stats.notification_skipped || 0),
          error: (vaxData.stats.error || 0) + (noVisitData.stats.error || 0)
        };
        
        setLogs(paginatedLogs);
        setTotal(combinedTotal);
        setTotalPages(Math.ceil(combinedTotal / limit));
        setStats(combinedStats);
        return;
      }
      
      // Single endpoint fetch for specific log types
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit)
      });
      if (eventTypeFilter) params.append('event_type', eventTypeFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      
      let baseUrl = `${API_BASE_URL}/api/v1/overdue-vaccine-triggers/logs`;
      if (logType === 'appointment_reminders') {
        baseUrl = API_ENDPOINTS.APPOINTMENT_REMINDER_LOGS.LIST;
        params.append('source', 'appointment_reminder');
      } else if (logType === 'needs_appointment') {
        baseUrl = API_ENDPOINTS.NO_VISIT_REMINDER_LOGS.LIST;
        // No 'source' param needed - this endpoint is dedicated to Appt Due workflow logs
      }
      const response = await fetch(`${baseUrl}?${params}`, { headers });
      
      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }
      
      const data: LogsResponse = await response.json();
      setLogs(data.logs);
      setTotal(data.total);
      setTotalPages(Math.ceil(data.total / limit));
      setStats(data.stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [page, eventTypeFilter, debouncedSearch, limit, logType]);

  // Fetch when filters change
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Show filters panel if any filter is active
  useEffect(() => {
    if (eventTypeFilter) {
      setShowFilters(true);
    }
  }, [eventTypeFilter]);

  const handleStatsCardClick = (filter: string) => {
    setEventTypeFilter(eventTypeFilter === filter ? '' : filter);
  };

  const clearFilters = () => {
    updateParams({ event_type: null, search: null, page: null });
  };

  const handleRefresh = () => {
    fetchLogs();
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const getStatusIcon = (eventType: string) => {
    const config = eventTypeConfig[eventType];
    if (!config) return <AlertCircle className="w-4 h-4 text-gray-400" />;
    return config.icon;
  };

  const getStatusBadge = (eventType: string) => {
    const config = eventTypeConfig[eventType] || eventTypeConfig.error;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getSelectedLabel = () => {
    const option = eventTypeOptions.find(o => o.value === eventTypeFilter);
    return option?.label || 'All Events';
  };


  return (
    <div className="h-full flex flex-col">
      {/* Header with Stats */}
      <div className="p-6 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary">
        {/* Stats Cards - Clickable to filter */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <button
            onClick={() => handleStatsCardClick('')}
            className={`text-left rounded-lg p-4 transition-all ${
              !eventTypeFilter
                ? 'bg-gray-100 dark:bg-dark-bg-tertiary ring-2 ring-gray-400 dark:ring-gray-500'
                : 'bg-gray-50 dark:bg-dark-bg-tertiary hover:bg-gray-100 dark:hover:bg-dark-bg-hover'
            }`}
          >
            <div className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
              {stats?.total || 0}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">Total Events</div>
          </button>
          
          <button
            onClick={() => handleStatsCardClick('notification_sent')}
            className={`text-left rounded-lg p-4 transition-all ${
              eventTypeFilter === 'notification_sent'
                ? 'bg-green-100 dark:bg-green-900/30 ring-2 ring-green-500'
                : 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'
            }`}
          >
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats?.notification_sent || 0}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">Sent</div>
          </button>
          
          <button
            onClick={() => handleStatsCardClick('error')}
            className={`text-left rounded-lg p-4 transition-all ${
              eventTypeFilter === 'error'
                ? 'bg-red-100 dark:bg-red-900/30 ring-2 ring-red-500'
                : 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30'
            }`}
          >
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {stats?.error || 0}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">Errors</div>
          </button>
          
          <button
            onClick={() => handleStatsCardClick('notification_skipped')}
            className={`text-left rounded-lg p-4 transition-all ${
              eventTypeFilter === 'notification_skipped'
                ? 'bg-amber-100 dark:bg-amber-900/30 ring-2 ring-amber-500'
                : 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30'
            }`}
          >
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
              {stats?.notification_skipped || 0}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">Skipped</div>
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={logType === 'overdue_vaccines'
                ? 'Search by message, vaccine, or pet name...'
                : 'Search by message, owner, or pet name...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 border rounded-lg transition-colors ${
              showFilters || eventTypeFilter
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                : 'border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {eventTypeFilter && (
              <span className="w-5 h-5 flex items-center justify-center bg-blue-600 text-white text-xs rounded-full">
                1
              </span>
            )}
          </button>
          
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-dark-text-muted">Log Type:</span>
              <div ref={logTypeDropdownRef} className="relative">
                <button
                  onClick={() => setLogTypeDropdownOpen(!logTypeDropdownOpen)}
                  className="flex items-center justify-between gap-2 min-w-[180px] px-3 py-1.5 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-sm text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
                >
                  <span>
                    {logType === 'all'
                      ? 'All Reminders'
                      : logType === 'overdue_vaccines'
                        ? 'Overdue Vaccines'
                        : logType === 'needs_appointment'
                          ? 'Appt Due Reminder'
                          : 'Appointment Reminders'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${logTypeDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {logTypeDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border rounded-lg shadow-lg overflow-hidden">
                    <button
                      onClick={() => {
                        setLogType('all');
                        setLogTypeDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        logType === 'all'
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium'
                          : 'text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                      }`}
                    >
                      All Reminders
                    </button>
                    <button
                      onClick={() => {
                        setLogType('overdue_vaccines');
                        setLogTypeDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        logType === 'overdue_vaccines'
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium'
                          : 'text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                      }`}
                    >
                      Overdue Vaccines
                    </button>
                    <button
                      onClick={() => {
                        setLogType('needs_appointment');
                        setLogTypeDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        logType === 'needs_appointment'
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium'
                          : 'text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                      }`}
                    >
                      Appt Due Reminder
                    </button>
                    <button
                      onClick={() => {
                        setLogType('appointment_reminders');
                        setLogTypeDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                        logType === 'appointment_reminders'
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium'
                          : 'text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                      }`}
                    >
                      Appointment Reminders
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-dark-text-muted">Event Type:</span>
              
              {/* Custom Tailwind Dropdown */}
              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center justify-between gap-2 min-w-[160px] px-3 py-1.5 border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg-tertiary text-sm text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
                >
                  <span>{getSelectedLabel()}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {dropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border rounded-lg shadow-lg overflow-hidden">
                    {eventTypeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setEventTypeFilter(option.value);
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          eventTypeFilter === option.value
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium'
                            : 'text-gray-700 dark:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {(eventTypeFilter || searchQuery) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary"
              >
                <X className="w-4 h-4" />
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading && logs.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-dark-text-muted">
            <AlertCircle className="w-12 h-12 mb-3 text-red-400" />
            <p className="text-lg font-medium">Error loading logs</p>
            <p className="text-sm">{error}</p>
            <button
              onClick={() => fetchLogs()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-dark-text-muted">
            <FileText className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-lg font-medium">No logs found</p>
            <p className="text-sm">
              {eventTypeFilter || searchQuery 
                ? 'Try adjusting your filters' 
                : 'Logs will appear when the notification system runs'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-dark-bg-tertiary sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Event
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Message
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Details
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className={eventTypeConfig[log.event_type]?.color || 'text-gray-400'}>
                        {getStatusIcon(log.event_type)}
                      </span>
                      {getStatusBadge(log.event_type)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900 dark:text-dark-text-primary">
                      {log.message}
                    </div>
                    {log.error_message && (
                      <div className="text-xs text-red-600 dark:text-red-400 mt-1 truncate max-w-md" title={log.error_message}>
                        {log.error_message}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-gray-500 dark:text-dark-text-muted max-w-xs">
                      {log.event_type === 'notification_scheduled' && log.details?.scheduled_for ? (
                        <span className="text-purple-600 dark:text-purple-400">
                          Fires at {formatDate(log.details.scheduled_for)}
                        </span>
                      ) : log.event_type === 'notification_skipped' && log.details?.info ? (
                        <span className="text-amber-600 dark:text-amber-400">
                          {log.details.info}
                        </span>
                      ) : (
                        <span className="truncate block">
                          {log.details?.vaccine_name || log.details?.vaccines?.join(', ') || log.details?.pet_name || '-'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-dark-text-secondary">
                      <Calendar className="w-3 h-3" />
                      {formatDate(log.created_at)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-dark-text-muted">
              Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, total)} of {total} entries
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1 || loading}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <span className="px-3 py-1.5 text-sm text-gray-700 dark:text-dark-text-secondary">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages || loading}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-dark-border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverdueVaccineLogsPage;
